import React, { useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Download, Search } from 'lucide-react';
import { exportCsv } from './exportCsv';
import { onActivateKey, ROW_FOCUS_RING } from '../../../utils/keyboard';
import { useScrollToTopOnChange } from '../../../utils/scroll';
import TablePager, { usePagedRows, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE } from '../../TablePager';

// The one table every report uses: sortable headers, client-side search and paging, CSV export.
// Columns: { key, label, align?: 'right', wrap?: true, render?(row), csv?(row), sortValue?(row) }.
//
// `wrap` lets a column break across lines. Cells are otherwise held on one line, which is right for
// codes, numbers and dates but is what forced the widest reports to scroll sideways: with every
// column refusing to break, the table's minimum width is the sum of its longest values and no
// amount of window gets you the last column. Marking the free-text columns (names, descriptions)
// wrappable lets the table shrink into the window instead. The header wraps with its column, since
// a long nowrap heading sets the same floor the cells would.
//
// The table scrolls inside its own box, held to the window's height with the header row pinned.
// Scrolling the page instead put the sideways scrollbar under the last row, so a wide report could
// only be scrolled across from the bottom of the page.
// Rows are plain objects straight off the API. Everything happens client-side because report
// payloads are already aggregated - a few hundred rows, not the raw records.
//
// Paging is the app-wide TablePager: the count above the table and again below it, a rows-per-page
// choice remembered per report, and the page buttons. `pageSize` is the size a report opens at; one
// that is not among the choices (the old default was 15) opens at the shared default instead.
// The window less the app header and the table's own title and footer bars, never so short that a
// small window shows only a row or two.
const TABLE_MAX_HEIGHT = 'max(16rem, calc(100vh - 15rem))';

// Which report the remembered rows-per-page belongs to. The CSV name carries the dates, the MRN or
// the medicine code ("PAMH-2610-0194-visits", "late-arrivals-2026-10-01-to-2026-10-14"); without
// them it names the kind of table, so the choice holds from one day, patient or medicine to the next.
const pagerKey = (csvName, title) => {
  if (!csvName) return String(title || 'table');
  return csvName.replace(/\d+/g, '').replace(/^([A-Z]+-+)+/, '').replace(/-+/g, '-').replace(/^-|-$/g, '') || csvName;
};

const ReportTable = ({ title, columns, rows, csvName, searchable = true, emptyText = 'No data in the selected range', footnote, onRowClick, pageSize = DEFAULT_PAGE_SIZE, noun = 'row', nounPlural }) => {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState(null); // { key, dir }
  const scrollRef = useRef(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) =>
      columns.some((c) => {
        // A column may say what text it matches (searchText); otherwise its own property, and for a
        // computed column with no property of its own, its CSV value.
        const v = c.searchText ? c.searchText(r) : r[c.key] !== undefined ? r[c.key] : c.csv ? c.csv(r) : undefined;
        return v !== null && v !== undefined && String(v).toLowerCase().includes(q);
      })
    );
  }, [rows, query, columns]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    const val = (r) => (col?.sortValue ? col.sortValue(r) : r[sort.key]);
    return [...filtered].sort((a, b) => {
      const av = val(a);
      const bv = val(b);
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sort, columns]);

  const paging = usePagedRows(sorted, {
    id: `report.${pagerKey(csvName, title)}`,
    initialSize: PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    resetOn: [query, sort],
  });
  const visible = paging.pageRows;
  // The rows scroll inside their own box, which keeps its position when the page turns.
  useScrollToTopOnChange(scrollRef, [paging.page, paging.pageSize, query, sort]);

  const toggleSort = (key) => {
    setSort((s) => (s?.key === key ? (s.dir === 'desc' ? { key, dir: 'asc' } : null) : { key, dir: 'desc' }));
  };

  return (
    <div className="bg-white rounded-2xl shadow overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-200 flex flex-wrap items-center gap-3">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center gap-2 ml-auto">
          {searchable && rows.length > PAGE_SIZE_OPTIONS[0] && (
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm w-44"
              />
            </div>
          )}
          {rows.length > 0 && csvName && (
            <button
              onClick={() => exportCsv(csvName, columns, sorted)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              title="Download this table as a CSV file"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
          )}
        </div>
      </div>
      <TablePager paging={paging} noun={noun} nounPlural={nounPlural} filteredFrom={query.trim() ? rows.length : undefined} className="px-5" />
      <div ref={scrollRef} className="overflow-auto" style={{ maxHeight: TABLE_MAX_HEIGHT }}>
        <table className="w-full">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  className={`sticky top-0 z-10 bg-gray-50 shadow-[inset_0_-1px_0_#e5e7eb] px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider select-none ${
                    c.wrap ? '' : 'whitespace-nowrap'
                  } ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {/* text-left is explicit because a button centres its text, which would show on a wrapped label. */}
                  <button
                    type="button"
                    onClick={() => toggleSort(c.key)}
                    className={`inline-flex items-center gap-1 w-full uppercase tracking-wider rounded hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                      c.align === 'right' ? 'justify-end text-right' : 'text-left'
                    }`}
                  >
                    {c.label}
                    {sort?.key === c.key &&
                      (sort.dir === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-sm text-gray-400">
                  {emptyText}
                </td>
              </tr>
            ) : (
              visible.map((row, i) => (
                <tr
                  key={i}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={onRowClick ? onActivateKey(() => onRowClick(row)) : undefined}
                  title={onRowClick ? 'Open details' : undefined}
                  className={`transition-colors ${
                    onRowClick ? `cursor-pointer hover:bg-emerald-50 ${ROW_FOCUS_RING}` : 'hover:bg-gray-50'
                  }`}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-3 py-2.5 text-sm text-gray-700 ${
                        c.wrap ? 'break-words' : 'whitespace-nowrap'
                      } ${c.align === 'right' ? 'text-right' : ''}`}
                    >
                      {c.render ? c.render(row) : row[c.key] ?? '—'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TablePager paging={paging} noun={noun} nounPlural={nounPlural} filteredFrom={query.trim() ? rows.length : undefined} position="bottom" className="px-5" />
      {footnote && (
        <div className="px-5 py-2.5 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">{footnote}</div>
      )}
    </div>
  );
};

export default ReportTable;
